import {
  Box,
  CloseButton,
  HStack,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spacer,
  useColorModeValue,
  useModalContext,
} from '@cardboard-ui/react';
import { SIDE_CAR_APP_FRAME_WIDTH } from 'apps/TenantApp/components/SideCarApp/layouts/DesktopSideCarAppLayout';
import { useReducedMotionConfig } from 'framer-motion';
import useSafeAreaInsetStyle from 'hooks/useSafeAreaInsetStyle';
import { useSidecarContext } from 'providers/SidecarProvider';
import React, { FC, PropsWithChildren, ReactNode } from 'react';
import { Layout, useActiveLayout } from 'utils/LayoutProvider';

interface Props {
  onClose: () => void;
  isOpen: boolean;
  title?: ReactNode;
  options?: ReactNode;
}

const ModalHeaderHeight = '40px';
export const ModalBodyHeight = `calc(100vh - ${ModalHeaderHeight})`;
const ModalWidth = '100vw';
const MobileModalWidth =
  'calc(100vw - env(safe-area-inset-left) - env(safe-area-inset-right))';
const DesktopWithoutChatWidth = `calc(${ModalWidth} - ${SIDE_CAR_APP_FRAME_WIDTH})`;

export const FolderItemModal: FC<PropsWithChildren<Props>> = ({
  onClose,
  isOpen,
  children,
}) => {
  const reducedMotion = useReducedMotionConfig();
  const transition = reducedMotion ? 'none' : 'all .3s';
  const { activeSidecarApp } = useSidecarContext();
  const chatIsOpen = activeSidecarApp === 'CHAT';
  const isMobile = useActiveLayout() !== Layout.DESKTOP;

  let widthBase = isMobile ? MobileModalWidth : ModalWidth;
  if (chatIsOpen && !isMobile) {
    widthBase = DesktopWithoutChatWidth;
  }

  const bgColor = useColorModeValue('gray.50', 'gray.800');
  useSafeAreaInsetStyle('top', { backgroundColor: bgColor });

  return (
    <Modal
      isOpen={isOpen}
      closeOnOverlayClick={false}
      scrollBehavior="inside"
      size="full"
      onClose={onClose}
      autoFocus={false}
      trapFocus={!chatIsOpen}
      blockScrollOnMount={!chatIsOpen}
      variant="fullMobile"
      motionPreset={reducedMotion ? 'none' : 'slideInBottom'}
    >
      <ModalOverlay
        style={{
          width: widthBase,
          maxWidth: widthBase,
          minWidth: widthBase,
          transition,
        }}
      />
      <ModalContent
        containerProps={{
          width: widthBase,
          maxWidth: widthBase,
          minWidth: widthBase,
          transition,
        }}
        width={widthBase}
        maxWidth={widthBase}
        minWidth={widthBase}
        data-qa="item-viewer"
        bg={bgColor}
        flex={1}
      >
        {children}
      </ModalContent>
    </Modal>
  );
};

interface AttachmentModalHeaderProps {
  title?: ReactNode;
  options?: ReactNode;
}

export const FolderItemModalHeader: FC<AttachmentModalHeaderProps> = ({
  title,
  options,
}) => {
  const { onClose } = useModalContext();

  return (
    <ModalHeader
      data-qa="item-viewer-header"
      fontSize="md"
      px={{ base: 3, md: 4 }}
      py={{ base: 1, md: 1 }}
    >
      <HStack>
        <Box w="full">{title}</Box>
        <Spacer />

        {options}
        <CloseButton onClick={onClose} />
      </HStack>
    </ModalHeader>
  );
};

export const FolderItemModalBody: FC<PropsWithChildren> = (props) => (
  <ModalBody
    p={0}
    display="flex"
    position="relative"
    overflow="hidden"
    {...props}
  />
);
